/**
 * Central onsite SEO config (titles, descriptions, noindex rules).
 * Aligns with checklist-style meta coverage similar to tbltechnerds/laravel-seo-toolkit / seo-autofix
 * (PageSpeed, GSC, IndexNow are run from that package against the live URL — see docs/ONSITE_SEO.md).
 */

import { blogPosts, type BlogPost } from '../data/blogPosts';
import { CUSTOMER_FAQ_ITEMS } from '../data/customerFaq';
import { getPremadeCategoryPage } from '../data/premadeCategoryPages';
import { getPremadeBrandPage } from '../data/premadeBrandPages';
import { slugToTitle } from '../utils/slugToTitle';
import { DEFAULT_WHITELABEL_BRAND, resolveWhiteLabelBrand } from './whiteLabel';

/** Resolve at import; never throw — a bad OEM env must not crash the client shell. */
function safeWhiteLabelBrand() {
  try {
    return resolveWhiteLabelBrand();
  } catch {
    return { ...DEFAULT_WHITELABEL_BRAND };
  }
}

const _brand = safeWhiteLabelBrand();

/** #1198 — product name from white-label env when enabled; else Foam CaseBuilder. */
export const SITE_NAME = _brand.productName;

/** Canonical / OG base — white-label origin or production default; SeoHead may use window.origin in dev. */
export const PRODUCTION_ORIGIN = _brand.canonicalOrigin;

const DEFAULT_TITLE = `${SITE_NAME} | Custom Foam Inserts & Protective Cases`;
const DEFAULT_DESCRIPTION =
  'Design CNC-ready custom foam inserts online for Pelican, SKB, Nanuk, and more. 2D editor, 3D preview, DXF export, and manufacturing quotes from Foam CaseBuilder.';

/** Open Graph / Twitter default share image (1200×630 PNG in `public/`). */
export const DEFAULT_OG_IMAGE_PATH = '/og-image.png';

/**
 * Default organization logo path — must stay a string literal matching
 * server `seoRoutes.ORGANIZATION_LOGO_PATH` (#900 parity guard).
 */
export const ORGANIZATION_LOGO_PATH = '/favicon.svg';

/**
 * #1198 — active logo href (OEM CDN URL or default path). Prefer this over
 * concatenating ORGANIZATION_LOGO_PATH when white-label may be on.
 */
export const ORGANIZATION_LOGO_HREF = _brand.logoUrl;

/** Join origin + path, or return absolute logo URLs unchanged (#1198). */
export function brandAssetUrl(origin: string, pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = origin.replace(/\/$/, '');
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${base}${path}`;
}

type SeoConfig = {
  title: string;
  description: string;
  /** Ask crawlers not to index (app shells, auth, private flows). */
  noindex?: boolean;
};

// KAN-SEO-002: exported so tests can iterate all public routes and verify sitemap coverage.
// Never add a route here without a matching sitemap.xml entry — seo.test.ts enforces it.
export const STATIC_ROUTES: Record<string, SeoConfig> = {
  '/': {
    title: `${SITE_NAME} | Custom Foam Inserts & Protective Cases`,
    description:
      'Protection through technology — design precision-cut foam inserts for equipment cases online. CNC manufacturing, MIL-spec compatible cases, and fast quotes.',
  },
  '/products': {
    title: `Protective Cases & Rackmount Solutions | ${SITE_NAME}`,
    description:
      'Browse protective electronic cases, rackmount transport cases, shock-mount options, and custom foam insert programs from Foam CaseBuilder.',
  },
  '/industries': {
    title: `Industries We Serve | Aerospace, Medical, Defense & More | ${SITE_NAME}`,
    description:
      'Foam and case solutions for aerospace, medical, military, government, telecom, industrial, and video production — engineered for field deployment.',
  },
  '/about': {
    title: `About ${SITE_NAME} | Custom Case & Foam Manufacturing`,
    description:
      'Learn how Foam CaseBuilder designs and manufactures custom foam inserts, container solutions, and integration packages for commercial and government customers.',
  },
  '/foam-guide': {
    title: `Foam & Case Selection Guide | ${SITE_NAME}`,
    description:
      'Choose the right case size, foam density, and layout for your equipment. Practical guidance for Pelican-class cases and custom dimensions.',
  },
  '/tutorial': {
    title: `Step-by-Step Tutorial | ${SITE_NAME}`,
    description:
      'Walk through the CaseBuilder workflow: pick a case, add shapes, preview in 3D, and request a manufacturing quote.',
  },
  '/contact': {
    title: `Contact Us | ${SITE_NAME}`,
    description:
      'Request a quote, ask about CNC foam lead times, or speak with our team about MIL-spec and custom case programs.',
  },
  '/faq': {
    title: `Frequently Asked Questions | ${SITE_NAME}`,
    description:
      'Answers about custom foam inserts, CaseBuilder features, shipping, materials, export formats, and ordering.',
  },
  '/gallery': {
    title: `Project Gallery | ${SITE_NAME}`,
    description:
      'See example foam layouts and case programs built with Foam CaseBuilder — inspiration for your next protective insert.',
  },
  '/compare': {
    title: `Foam Case Design: How TBL Compares | ${SITE_NAME}`,
    description:
      'How Foam CaseBuilder compares to online designer+cart tools, custom-quote foam shops, pre-cut catalogs, and DIY CAD + CNC workflows.',
  },
  '/for-case-makers': {
    title: `Foam Insert Designer for Case & Foam Shops | ${SITE_NAME}`,
    description:
      'Let customers design their own foam inserts on your website. Your shop receives a CNC-ready DXF with every request, on your domain and your case catalog.',
  },
  '/blog': {
    title: `The Case Files — Blog & Guides | ${SITE_NAME}`,
    description:
      'Technical guides on foam density, MIL-STD ratings, measuring cases, PE vs EVA, and multi-layer insert design.',
  },
  '/shop': {
    title: `Shop Cases & Accessories | ${SITE_NAME}`,
    description:
      'Shop compatible protective cases and accessories that pair with Foam CaseBuilder custom foam inserts.',
  },
  '/design': {
    title: `Design Your Custom Foam Insert | Case Library & Dimensions | ${SITE_NAME}`,
    description:
      'Select a Pelican, SKB, Nanuk, or other case from our library — or enter custom dimensions — then open the foam editor.',
  },
  '/premade-cases': {
    title: `Premade Case Builds Portfolio | 200+ Finished Foam Layouts | ${SITE_NAME}`,
    description:
      'Browse 200+ finished foam insert builds for firearms, cameras, drones, medical equipment, and more. Copy a layout as your starting point.',
  },
  '/pro-design': {
    title: `Pro Design Service | Expert Custom Foam Design | ${SITE_NAME}`,
    description:
      'Skip the design tool — our engineers build your custom foam insert for you. Send photos and a case model; get a production-ready DXF, 3D preview, revision rounds, and a manufacturing quote. Tiers from $100.',
  },
};

export function normalizePathname(pathname: string): string {
  if (!pathname) return '/';
  const trimmed = pathname.replace(/\/+$/, '') || '/';
  return trimmed;
}

export function resolveSeo(pathname: string): SeoConfig {
  const path = normalizePathname(pathname);

  if (path.startsWith('/editor')) {
    return {
      title: `Foam Editor | ${SITE_NAME}`,
      description:
        'Interactive foam layout editor: drag-and-drop library shapes, 3D preview, DXF/SVG export, and manufacturing quotes.',
      noindex: true,
    };
  }
  if (path.startsWith('/designs')) {
    return {
      title: `My Designs | ${SITE_NAME}`,
      description: 'View and manage your saved Foam CaseBuilder designs.',
      noindex: true,
    };
  }
  if (path.startsWith('/profile')) {
    return {
      title: `Profile | ${SITE_NAME}`,
      description: 'Manage your Foam CaseBuilder account and profile.',
      noindex: true,
    };
  }
  if (path.startsWith('/quote')) {
    return {
      title: `Quote | ${SITE_NAME}`,
      description: 'Review pricing and submit a quote request for your custom foam insert.',
      noindex: true,
    };
  }
  if (path.startsWith('/share')) {
    return {
      title: `Shared Design | ${SITE_NAME}`,
      description: 'View a shared Foam CaseBuilder design.',
      noindex: true,
    };
  }
  if (path.startsWith('/login')) {
    return {
      title: `Sign In | ${SITE_NAME}`,
      description: 'Sign in to Foam CaseBuilder to save designs and request quotes.',
      noindex: true,
    };
  }

  // Category + brand landing pages take precedence over detail slugs.
  const premadeSlugMatch = path.match(/^\/premade-cases\/([^/]+)$/);
  if (premadeSlugMatch) {
    const slug = premadeSlugMatch[1];
    const categoryPage = getPremadeCategoryPage(slug);
    if (categoryPage) {
      return {
        title: `${categoryPage.title} | ${SITE_NAME}`,
        description: categoryPage.metaDescription,
      };
    }
    const brandPage = getPremadeBrandPage(slug);
    if (brandPage) {
      return {
        title: `${brandPage.title} | ${SITE_NAME}`,
        description: brandPage.metaDescription,
      };
    }

    // KAN-SEO-003: individual case build pages — unique meta per slug.
    const humanName = slugToTitle(slug);
    return {
      title: `${humanName} Foam Layout | Premade Build | ${SITE_NAME}`,
      description: `See the finished foam insert layout for the ${humanName}. Browse 200+ premade foam builds and copy any layout as your CaseBuilder starting point in minutes.`,
    };
  }

  const blogMatch = path.match(/^\/blog\/([^/]+)$/);
  if (blogMatch) {
    const slug = blogMatch[1];
    const post = blogPosts.find((p) => p.slug === slug);
    if (post) {
      return {
        title: `${post.title} | ${SITE_NAME}`,
        description: post.excerpt,
      };
    }
  }

  const staticHit = STATIC_ROUTES[path];
  if (staticHit) return { ...staticHit };

  // #67: any path reaching here is unmatched (a 404) — every real route is in
  // STATIC_ROUTES or an explicit branch above. noindex so soft-404s aren't
  // indexed; SeoHead emits the single robots tag from this flag.
  return {
    title: `Page Not Found | ${SITE_NAME}`,
    description: DEFAULT_DESCRIPTION,
    noindex: true,
  };
}

export function organizationJsonLd(origin: string): object {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: SITE_NAME,
        url: origin,
        logo: brandAssetUrl(origin, ORGANIZATION_LOGO_HREF),
        description: DEFAULT_DESCRIPTION,
      },
      {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: origin,
        description: DEFAULT_DESCRIPTION,
      },
    ],
  };
}

/**
 * BreadcrumbList JSON-LD for deep pages. Google renders it as a breadcrumb trail in
 * search results (better SERP presence + click-through). `items` is the ordered trail
 * from the site root to the current page, each with an absolute `url`.
 */
export function breadcrumbListJsonLd(items: ReadonlyArray<{ name: string; url: string }>): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * KAN-SEO-FAQ-001 (issue #32): FAQPage JSON-LD for /faq and /contact#faq.
 *
 * Google rich-results render FAQPage as a collapsible Q&A block under the
 * blue link → 2-5x CTR vs plain SERP listing for long-tail "how-to" queries.
 * The 10-item catalog in `CUSTOMER_FAQ_ITEMS` is the single source of truth;
 * this helper just adapts it to the schema.org shape Google expects.
 *
 * Spec: https://developers.google.com/search/docs/appearance/structured-data/faqpage
 *
 * Verification post-deploy:
 *   curl -s https://foamcasebuilder.com/faq | grep -o '"@type":"FAQPage"'
 *   https://search.google.com/test/rich-results?url=https://foamcasebuilder.com/faq
 */
export interface FaqPageStructuredData {
  '@context': 'https://schema.org';
  '@type': 'FAQPage';
  mainEntity: ReadonlyArray<{
    '@type': 'Question';
    name: string;
    acceptedAnswer: {
      '@type': 'Answer';
      text: string;
    };
  }>;
}

export function getFaqPageStructuredData(): FaqPageStructuredData {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: CUSTOMER_FAQ_ITEMS.map((item) => ({
      '@type': 'Question' as const,
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer' as const,
        text: item.a,
      },
    })),
  };
}

/**
 * Resolve the canonical origin for absolute URLs in JSON-LD / OG tags.
 * SSR / production builds → PRODUCTION_ORIGIN; dev → window.location.origin.
 * Single source of truth shared by SeoHead and the blog page injectors so
 * canonical/OG/JSON-LD URLs never disagree.
 */
export function resolveOrigin(): string {
  if (typeof window === 'undefined') return PRODUCTION_ORIGIN;
  if (import.meta.env.PROD) return PRODUCTION_ORIGIN;
  return window.location.origin;
}

/**
 * KAN-SEO-BLOGPOSTING-001 (issue #33): BlogPosting JSON-LD for /blog/:slug.
 *
 * Google promotes Article/BlogPosting rich-results (headline + date + author)
 * under the SERP listing. Required-field discipline that this helper enforces:
 *   - headline ≤ 110 chars (Google truncates beyond that)
 *   - image is an ABSOLUTE URL (relative paths are rejected by the validator)
 *   - datePublished/dateModified are ISO-8601
 *   - publisher is an Organization with an ImageObject logo at an absolute URL
 * wordCount is computed from the real post body (blogPosts.ts), not guessed.
 *
 * Spec: https://developers.google.com/search/docs/appearance/structured-data/article
 *
 * Verification post-deploy:
 *   curl -s https://foamcasebuilder.com/blog/foam-density-guide | grep -o '"@type":"BlogPosting"'
 *   https://search.google.com/test/rich-results?url=https://foamcasebuilder.com/blog/foam-density-guide
 */
const MAX_HEADLINE_LENGTH = 110;

export interface BlogPostingStructuredData {
  '@context': 'https://schema.org';
  '@type': 'BlogPosting';
  headline: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: {
    '@type': 'Organization';
    name: string;
  };
  publisher: {
    '@type': 'Organization';
    name: string;
    logo: {
      '@type': 'ImageObject';
      url: string;
    };
  };
  mainEntityOfPage: {
    '@type': 'WebPage';
    '@id': string;
  };
  description: string;
  articleSection: string;
  wordCount: number;
}

export function getBlogPostingStructuredData(
  post: BlogPost,
  origin = resolveOrigin(),
): BlogPostingStructuredData {
  const wordCount = post.body.join(' ').trim().split(/\s+/).filter(Boolean).length;
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title.slice(0, MAX_HEADLINE_LENGTH),
    image: post.imageUrl ? `${origin}${post.imageUrl}` : `${origin}${DEFAULT_OG_IMAGE_PATH}`,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Organization',
      name: post.author,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: brandAssetUrl(origin, ORGANIZATION_LOGO_HREF),
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${origin}/blog/${post.slug}`,
    },
    description: post.excerpt,
    articleSection: post.category,
    wordCount,
  };
}

/**
 * KAN-SEO-BLOGPOSTING-001 (issue #33): Blog + ItemList JSON-LD for the /blog
 * index. Gives Google an ordered list of the published posts (1-based
 * positions, absolute URLs) so the blog can surface as a structured list.
 */
export interface BlogListStructuredData {
  '@context': 'https://schema.org';
  '@type': 'Blog';
  url: string;
  mainEntity: {
    '@type': 'ItemList';
    itemListElement: ReadonlyArray<{
      '@type': 'ListItem';
      position: number;
      url: string;
    }>;
  };
}

export function getBlogListStructuredData(
  posts: BlogPost[],
  origin = resolveOrigin(),
): BlogListStructuredData {
  return {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    url: `${origin}/blog`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: posts.map((post, index) => ({
        '@type': 'ListItem' as const,
        position: index + 1,
        url: `${origin}/blog/${post.slug}`,
      })),
    },
  };
}

/**
 * KAN-COMP-PRODESIGN-001 (issue #34): Service JSON-LD for /pro-design.
 *
 * The paid expert-design tier is a service, not a product — schema.org/Service
 * lets Google surface the provider, service type, area served, and the tiered
 * price points (Standard $100 / Complex $250 / Multi-layer + B2B $500+) as a
 * rich offer block. Prices are emitted as strings per Google's Offer guidance
 * (numeric-looking strings are the safest cross-validator format) and the
 * provider logo is an ABSOLUTE URL (relative paths are rejected by the validator).
 *
 * Spec: https://developers.google.com/search/docs/appearance/structured-data
 *
 * Verification post-deploy:
 *   curl -s https://foamcasebuilder.com/pro-design | grep -o '"@type":"Service"'
 *   https://search.google.com/test/rich-results?url=https://foamcasebuilder.com/pro-design
 */
export interface ProDesignServiceStructuredData {
  '@context': 'https://schema.org';
  '@type': 'Service';
  name: string;
  serviceType: 'Custom foam case design';
  areaServed: 'Worldwide';
  description: string;
  provider: {
    '@type': 'Organization';
    name: string;
    url: string;
    logo: string;
  };
  offers: ReadonlyArray<{
    '@type': 'Offer';
    name: string;
    price: string;
    priceCurrency: 'USD';
    description: string;
  }>;
}

/** Pricing tiers for the Pro Design service — single source of truth shared by the page and JSON-LD. */
export const PRO_DESIGN_TIERS = [
  {
    name: 'Standard',
    price: '100',
    description: 'Single-layer foam insert for one piece of gear — production-ready cut files + 3D preview.',
  },
  {
    name: 'Complex',
    price: '250',
    description: 'Multi-item layout with intricate cutouts, finger holes, and labels.',
  },
  {
    name: 'Multi-layer / B2B',
    price: '500',
    description: 'Stacked multi-layer foam, kitting, and volume programs for businesses.',
  },
] as const;

export function getProDesignServiceStructuredData(
  origin = resolveOrigin(),
): ProDesignServiceStructuredData {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Pro Design Service',
    serviceType: 'Custom foam case design',
    areaServed: 'Worldwide',
    description:
      'Expert engineer-built custom foam insert design: send photos and a case model, receive a production-ready DXF, 3D preview, revision rounds, and a manufacturing quote.',
    provider: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: origin,
      logo: brandAssetUrl(origin, ORGANIZATION_LOGO_HREF),
    },
    offers: PRO_DESIGN_TIERS.map((tier) => ({
      '@type': 'Offer' as const,
      name: tier.name,
      price: tier.price,
      priceCurrency: 'USD' as const,
      description: tier.description,
    })),
  };
}

export { DEFAULT_DESCRIPTION, DEFAULT_TITLE };
